import { memo, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { isNil, isEmpty, noop } from 'lodash';
import { useFormik } from 'formik';
// Import Types
import { DeliveryMethodData } from '../CJBestMessageSender/types';
// Import UI Components
import { ExtendedInput } from '@geneui/components';
// Import Components
import OptionList from 'components/common/OptionList';
// Import Helpers
import { customMomentWithoutTimezoneConversion } from 'services/Helpers';
// Import Services
import { TemplateHttpService } from 'services/http';
import { getCustomerJourneyFormFieldTranslatableErrorText } from 'services/customerJourney/helpers';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
import useAutocompleteRequest from 'hooks/UseAutocompleteRequest';
import { usePartnerDateFormats } from 'hooks/UsePartnerDateFormats';
// Import Constants
import { l, DEFAULT_SORTING, FilterComparisons } from 'constants/common';
// Import Enums
import { DeliveryMethodEnum, LabelAppearanceEnum } from 'enums/common';
// Import Validation
import { CJTemplateIdValidationSchema } from 'validators/schemas.validation';
// Import Styles
import styles from './cjTemplateId.module.scss';

const { Equals, In, Like } = FilterComparisons;

const NON_DIGIT_REGEX = /\D/g;

interface Props {
    defaultValue?: DeliveryMethodData['template'];
    getUpdate: (data: DeliveryMethodData['template']) => void;
    option: AnyObject;
    isDisabled?: boolean;
    clearable?: boolean;
}

const CJTemplateId: React.FC<Props> = (props) => {
    const { defaultValue = { value: null }, getUpdate, option, isDisabled = false, clearable = false } = props;
    const { t } = useTranslation();
    const { doPostRequest, doGetRequest } = useRequest();
    const { getAutocompleteRequest, cancelRequest } = useAutocompleteRequest();
    const { partnerDateTimeWithoutSecondsFormat } = usePartnerDateFormats();

    const { DeliveryMethod = DeliveryMethodEnum.PushNotification, DisplayName } = option?.CustomAttributes ?? {};
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingList, setIsLoadingList] = useState(false);
    const [listData, setListData] = useState([]);
    const [searchByNameValue, setSearchByNameValue] = useState('');
    const [searchByIdValue, setSearchByIdValue] = useState('');
    const [selectedTemplateData, setSelectedTemplateData] = useState<AnyObject>({ TemplateId: defaultValue?.value });
    const [isVisiblePopover, setIsVisiblePopover] = useState(false);
    const formik = useFormik({
        initialValues: {
            value: defaultValue?.value,
        },
        onSubmit: noop,
        validationSchema: CJTemplateIdValidationSchema(option),
    });
    const { values, setFieldValue, errors, submitForm } = formik;
    const { value } = values;
    const { getTemplateById } = useCancelableRequests({
        getTemplateById: TemplateHttpService.getTemplate,
    });
    // factory reference: a new request object is created per call through useAutocompleteRequest
    const getTemplateList = TemplateHttpService.getTemplateList;

    //TODO ART remove this block if everything is fine

    // useEffect(() => {
    //     getUpdate({ value });
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [value]);

    const templateListGetterMethod = useCallback(
        (searchValues = { name: searchByNameValue || '', id: searchByIdValue || '' }) => {
            const { name, id } = searchValues;
            setIsLoadingList(true);

            const requestObj = getAutocompleteRequest(getTemplateList);

            doPostRequest(requestObj.request, {
                requestBody: {
                    Filters: [
                        { Name: 'State', Comparision: Equals, Values: [1] },
                        { Name: 'DeliveryMethod', Comparision: In, Values: [DeliveryMethod] },
                        { Name: 'Name', Comparision: Like, Values: [name] },
                        { Name: 'TemplateId', Comparision: Like, Values: id === '' ? [] : [+id] },
                    ].filter(Boolean),
                    Pageing: { PageSize: 100, PageNumber: 1 },
                    Sorting: DEFAULT_SORTING,
                },
                successCallback: (data) => {
                    setListData(
                        !isNil(data?.Data)
                            ? data.Data?.map((item) => ({
                                  ...item,
                                  formattedCreatedDate: customMomentWithoutTimezoneConversion(item.CreatedDate).format(
                                      partnerDateTimeWithoutSecondsFormat,
                                  ),
                                  formattedChildInfo: item.ChildItemsInfo.length
                                      ? item.ChildItemsInfo.map(({ Language }) => Language.toUpperCase()).join(' ')
                                      : t(l.Default),
                              }))
                            : [],
                    );
                    setIsLoadingList(false);
                },
                errorCallback: () => setIsLoadingList(false),
            });
        },
        [
            DeliveryMethod,
            doPostRequest,
            getAutocompleteRequest,
            getTemplateList,
            partnerDateTimeWithoutSecondsFormat,
            searchByIdValue,
            searchByNameValue,
            t,
        ],
    );

    const handleSearchByTemplateIdChange = useCallback(
        ({ target }) => {
            const value = target.value.replace(NON_DIGIT_REGEX, '');
            if (value === searchByIdValue) return;

            setSearchByIdValue(value);
            templateListGetterMethod({ name: searchByNameValue, id: value });
        },
        [searchByIdValue, searchByNameValue, templateListGetterMethod],
    );

    const handleSearchByTemplateNameChange = useCallback(
        ({ target }) => {
            setSearchByNameValue(target.value);
            templateListGetterMethod({ name: target.value, id: searchByIdValue });
        },
        [searchByIdValue, templateListGetterMethod],
    );

    const renderCustomSearch = useCallback(() => {
        return (
            <div className={styles.searchWrapper}>
                <ExtendedInput
                    label={t(l.Id)}
                    labelAppearance="swap"
                    placeholder={t(l.Id)}
                    value={searchByIdValue}
                    onChange={handleSearchByTemplateIdChange}
                    canClear={true}
                />
                <ExtendedInput
                    label={t(l.Name)}
                    labelAppearance="swap"
                    placeholder={t(l.Name)}
                    value={searchByNameValue}
                    onChange={handleSearchByTemplateNameChange}
                    canClear={true}
                />
            </div>
        );
    }, [handleSearchByTemplateIdChange, handleSearchByTemplateNameChange, searchByIdValue, searchByNameValue, t]);

    const init = () => {
        submitForm();
        templateListGetterMethod();

        if (!isNil(defaultValue?.value) && !isEmpty(defaultValue?.value.toString())) {
            setIsLoading(true);
            doGetRequest(getTemplateById.request, {
                queryString: { templateId: defaultValue?.value },
                successCallback: (Data) => {
                    const { TemplateId, Name } = Data;
                    setSelectedTemplateData({ TemplateId, Name });
                },
            }).then(() => {
                setIsLoading(false);
            });
        }
    };

    const cleanUp = () => {
        return () => {
            cancelRequest('CJTemplateId:getTemplateList');
        };
    };
    useEffect(() => {
        submitForm();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [option]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, []);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(cleanUp, []);

    const popoverToggler = () => {
        setIsVisiblePopover(!isVisiblePopover);
    };

    const handleTemplateSelect = (data) => {
        setSelectedTemplateData(data);
        setFieldValue('value', data.TemplateId);
        getUpdate({ value: data.TemplateId });
        setIsVisiblePopover(false);
    };

    const handleTemplateClear = () => {
        setSelectedTemplateData({ TemplateId: null });
        setFieldValue('value', null);
        getUpdate({ value: null });
        setIsVisiblePopover(false);
    };

    return (
        <OptionList
            listMinWidth={700}
            labelAppearance={LabelAppearanceEnum.Swap}
            placeholder={t(l[DisplayName])}
            optionLabel={selectedTemplateData?.Name}
            toggleHandler={popoverToggler}
            isVisible={isVisiblePopover}
            setIsVisible={setIsVisiblePopover}
            keys={['Name', 'formattedChildInfo', 'formattedCreatedDate', 'TemplateId']}
            titles={[t(l.Name), t(l.Language), t(l.CreatedDate), t(l.Id)]}
            selectedPropertyKey="TemplateId"
            list={listData}
            rowCount={listData.length}
            optionValue={value}
            optionSelectHandler={handleTemplateSelect}
            clearable={clearable}
            onClear={handleTemplateClear}
            contentTop={45}
            contentWrapperClassName="cj-option-list-content-wrapper"
            isLoading={isLoading}
            isLoadingList={isLoadingList}
            isValid={!errors.value}
            errorText={getCustomerJourneyFormFieldTranslatableErrorText(t, errors?.value)}
            isDisabled={isDisabled}
            renderCustomSearch={renderCustomSearch}
        />
    );
};

export default memo(CJTemplateId);
